/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { RefObject } from 'preact';
import type { MutableRef } from 'preact/hooks';
import type { Keys } from '#utils/UNSAFE_keys';
import type { ItemActionDetail, SelectionDetail } from '#UNSAFE_Collection';
import type { CollectionData } from '#PRIVATE_TableCommon/Common.types';
import type { ListSelectionMode } from '../List.types';
import type { ActiveCell, ActiveCellDetail, ScrollDetail, ViewportInfo } from '../List';

import { useCallback, useRef } from 'preact/hooks';
import {
  getRowActionGestureContext,
  handleRowActionGesture
} from '#PRIVATE_TableCommon/utils/ActionUtils';
import { getActionContext } from '../utils/ListActionUtils';
import { getElementFromCell, isAutoFocusCell, LIST_ROW_SELECTOR } from '../utils/ListDomUtils';
import {
  handleAdjacentRowGesture,
  handlePageRowGesture,
  isReorderGesture
} from '../utils/ListNavigationUtils';
import { handleExtendSelectionGesture, handleSelectionGesture } from '../utils/ListSelectionUtils';

type UseKeyboardHandlingProps<K, D> = {
  rootRef: RefObject<HTMLDivElement>;
  viewportInfo: ViewportInfo;
  listId: string;
  listData: CollectionData<K, D>[];
  isTabbableMode: boolean;
  selectionExtensionElementRef: MutableRef<HTMLElement | undefined>;
  onActiveCellChanged: (
    detail: ActiveCellDetail<K>,
    isAutoFocus?: boolean,
    isSelectionExtension?: boolean,
    skipScrollCellIntoView?: boolean
  ) => void;
  activeCell?: ActiveCell<K>;
  isDropDownSelection: boolean;
  selectionMode?: ListSelectionMode;
  selectedKeys?: Keys<K>;
  onListSelectionChange?: (detail: SelectionDetail<K>, isExtendableSelection: boolean) => void;
  onItemAction?: (detail: ItemActionDetail<K, D>) => void;
  updateDeferredScrollDetail: (scrollDetail: ScrollDetail<K>) => void;
  reorderKeyboardProps: { onKeyDown?: (e: KeyboardEvent) => void };
  isReorderEnabled: boolean;
};

/**
 * Hook that manages keyboard interactions on the Preact TableList
 */
export function useKeyboardHandling<K extends string | number, D>({
  rootRef,
  viewportInfo,
  listId,
  listData,
  isTabbableMode,
  selectionExtensionElementRef,
  activeCell,
  onActiveCellChanged,
  isDropDownSelection,
  selectionMode,
  selectedKeys,
  onListSelectionChange,
  onItemAction,
  updateDeferredScrollDetail,
  reorderKeyboardProps,
  isReorderEnabled
}: UseKeyboardHandlingProps<K, D>) {
  // state for the key that a space or enter keydown occurrs on for matching keyup handling
  const triggerDownKeyRef = useRef<K | undefined>();

  // Keydown handler for the outer List. This handles all internal
  // keyboard navigation for the List.
  const _keyDownHandler = useCallback(
    (event: KeyboardEvent) => {
      const rootElement = rootRef.current;
      const scrollerElement = viewportInfo.viewportConfig.scrollerRef.current;
      if (rootElement != null && scrollerElement != null) {
        const keyHandlers: Record<string, () => void> = {
          ' ': function () {
            if (activeCell != null) {
              triggerDownKeyRef.current = activeCell.rowKey;
              if (!isTabbableMode) {
                // prevent default action on space to avoid scrolling during a selection
                event.preventDefault();
              }
              if (
                onItemAction != null &&
                getRowActionGestureContext(
                  listId,
                  listData,
                  event.target as HTMLElement,
                  LIST_ROW_SELECTOR,
                  getActionContext,
                  activeCell
                ) != null
              ) {
                event.stopPropagation();
              }
            }
          },
          Enter: function () {
            if (activeCell != null) {
              triggerDownKeyRef.current = activeCell.rowKey;
              if (activeCell.type === 'data') {
                if (isDropDownSelection && !isTabbableMode) {
                  event.preventDefault();
                }
                if (
                  onItemAction != null &&
                  getRowActionGestureContext(
                    listId,
                    listData,
                    event.target as HTMLElement,
                    LIST_ROW_SELECTOR,
                    getActionContext,
                    activeCell
                  ) != null
                ) {
                  event.stopPropagation();
                }
              }
            }
          },
          ArrowUp: function () {
            if (isReorderEnabled && !isTabbableMode && isReorderGesture(event)) {
              if (activeCell?.type === 'data') {
                reorderKeyboardProps.onKeyDown?.(event);
                updateDeferredScrollDetail({
                  cell: activeCell,
                  locationX: 'inView',
                  locationY: 'inView'
                });
              }
              return;
            }
            if (
              activeCell != null &&
              (!isTabbableMode || isAutoFocusCell(rootElement, listId, activeCell))
            ) {
              let isExtendSelectionGesture = false;
              if (
                !isTabbableMode &&
                activeCell.type === 'data' &&
                event.shiftKey &&
                onListSelectionChange != null &&
                (selectionMode === 'multiple' || selectionMode === 'multipleToggle')
              ) {
                handleExtendSelectionGesture(
                  rootElement,
                  listId,
                  rootElement,
                  true,
                  activeCell,
                  selectionExtensionElementRef,
                  onListSelectionChange,
                  isDropDownSelection,
                  updateDeferredScrollDetail,
                  selectionMode,
                  selectedKeys
                );
                isExtendSelectionGesture = true;
              }
              if (
                !isExtendSelectionGesture ||
                isDropDownSelection ||
                selectionMode === 'multipleToggle'
              ) {
                handleAdjacentRowGesture(
                  rootElement,
                  listId,
                  activeCell,
                  onActiveCellChanged,
                  true,
                  isExtendSelectionGesture,
                  updateDeferredScrollDetail
                );
              }
              event.preventDefault();
            }
          },
          ArrowDown: function () {
            if (isReorderEnabled && !isTabbableMode && isReorderGesture(event)) {
              if (activeCell?.type === 'data') {
                reorderKeyboardProps.onKeyDown?.(event);
                updateDeferredScrollDetail({
                  cell: activeCell,
                  locationX: 'inView',
                  locationY: 'inView'
                });
              }
              return;
            }
            if (
              activeCell != null &&
              (!isTabbableMode || isAutoFocusCell(rootElement, listId, activeCell))
            ) {
              let isExtendSelectionGesture = false;
              if (
                !isTabbableMode &&
                activeCell.type === 'data' &&
                event.shiftKey &&
                onListSelectionChange != null &&
                (selectionMode === 'multiple' || selectionMode === 'multipleToggle')
              ) {
                handleExtendSelectionGesture(
                  rootElement,
                  listId,
                  rootElement,
                  false,
                  activeCell,
                  selectionExtensionElementRef,
                  onListSelectionChange,
                  isDropDownSelection,
                  updateDeferredScrollDetail,
                  selectionMode,
                  selectedKeys
                );
                isExtendSelectionGesture = true;
              }
              if (
                !isExtendSelectionGesture ||
                isDropDownSelection ||
                selectionMode === 'multipleToggle'
              ) {
                handleAdjacentRowGesture(
                  rootElement,
                  listId,
                  activeCell,
                  onActiveCellChanged,
                  false,
                  isExtendSelectionGesture,
                  updateDeferredScrollDetail
                );
              }
              event.preventDefault();
            }
          },
          PageUp: function () {
            if (
              activeCell != null &&
              (!isTabbableMode || isAutoFocusCell(rootElement, listId, activeCell))
            ) {
              handlePageRowGesture(
                rootElement,
                scrollerElement,
                viewportInfo,
                listId,
                activeCell,
                onActiveCellChanged,
                true,
                updateDeferredScrollDetail
              );
              event.preventDefault();
            }
          },
          PageDown: function () {
            if (
              activeCell != null &&
              (!isTabbableMode || isAutoFocusCell(rootElement, listId, activeCell))
            ) {
              handlePageRowGesture(
                rootElement,
                scrollerElement,
                viewportInfo,
                listId,
                activeCell,
                onActiveCellChanged,
                false,
                updateDeferredScrollDetail
              );
              event.preventDefault();
            }
          },
          Home: function () {
            if (
              activeCell != null &&
              (!isTabbableMode || isAutoFocusCell(rootElement, listId, activeCell))
            ) {
              event.preventDefault();
            }
          },
          End: function () {
            if (
              activeCell != null &&
              (!isTabbableMode || isAutoFocusCell(rootElement, listId, activeCell))
            ) {
              event.preventDefault();
            }
          }
        };
        if (Object.keys(keyHandlers).includes(event.key)) {
          keyHandlers[event.key]();
        }
      }
    },
    [
      rootRef,
      viewportInfo,
      listId,
      listData,
      isTabbableMode,
      selectionExtensionElementRef,
      activeCell,
      onActiveCellChanged,
      isDropDownSelection,
      selectionMode,
      selectedKeys,
      onListSelectionChange,
      onItemAction,
      updateDeferredScrollDetail,
      reorderKeyboardProps,
      isReorderEnabled
    ]
  );

  // Keyup handler for the outer List.
  const _keyUpHandler = useCallback(
    (event: KeyboardEvent) => {
      const rootElement = rootRef.current;
      const scrollerElement = viewportInfo.viewportConfig.scrollerRef.current;
      if (rootElement != null && scrollerElement != null) {
        const keyHandlers: Record<string, () => void> = {
          ' ': function () {
            if (activeCell != null) {
              if (triggerDownKeyRef.current === activeCell.rowKey) {
                if (!isTabbableMode) {
                  if (onListSelectionChange != null) {
                    handleSelectionGesture(
                      listId,
                      getElementFromCell(rootElement, listId, activeCell) as HTMLElement,
                      true,
                      onListSelectionChange,
                      isDropDownSelection,
                      selectionMode,
                      selectedKeys
                    );
                  }
                }
                if (
                  onItemAction != null &&
                  handleRowActionGesture(
                    listId,
                    listData,
                    onItemAction,
                    event.target as HTMLElement,
                    LIST_ROW_SELECTOR,
                    getActionContext,
                    activeCell
                  )
                ) {
                  event.stopPropagation();
                }
              }
              triggerDownKeyRef.current = undefined;
            }
          },
          Enter: function () {
            if (activeCell != null && triggerDownKeyRef.current === activeCell.rowKey) {
              if (activeCell.type === 'data') {
                if (isDropDownSelection && !isTabbableMode) {
                  if (onListSelectionChange != null) {
                    handleSelectionGesture(
                      listId,
                      getElementFromCell(rootElement, listId, activeCell) as HTMLElement,
                      true,
                      onListSelectionChange,
                      isDropDownSelection,
                      selectionMode,
                      selectedKeys
                    );
                  }
                }
                if (
                  onItemAction != null &&
                  handleRowActionGesture(
                    listId,
                    listData,
                    onItemAction,
                    event.target as HTMLElement,
                    LIST_ROW_SELECTOR,
                    getActionContext,
                    activeCell
                  )
                ) {
                  event.stopPropagation();
                }
              }
            }
            triggerDownKeyRef.current = undefined;
          }
        };
        if (Object.keys(keyHandlers).includes(event.key)) {
          keyHandlers[event.key]();
        }
      }
    },
    [
      rootRef,
      viewportInfo,
      listId,
      listData,
      isTabbableMode,
      activeCell,
      isDropDownSelection,
      selectionMode,
      selectedKeys,
      onListSelectionChange,
      onItemAction
    ]
  );

  return { onKeyDown: _keyDownHandler, onKeyUp: _keyUpHandler };
}
